import * as React from "react";
import { NavigateFunction } from "react-router-dom";
import { GenetecAlarmSetupDialog } from "./GenetecAlarmSetupDialog";
import {
  GenetecAlarmPollOptions,
  GenetecAlarmSetup,
  fetchGenetecAlarmSetup,
  shouldPromptGenetecAlarmSetup,
} from "./genetecAlarmSetupApi";

/**
 * End-of-auto-add prompt for Genetec sites (ENG-2219). Asks the backend what
 * it would set up and only opens the dialog when there is work to do
 * (`shouldPromptGenetecAlarmSetup`); otherwise — or if the GET fails — it
 * calls `onDone` straight away so the onboarding flow carries on. `onDone`
 * runs exactly once, so "Not now" never re-prompts within the same flow.
 */
export const GenetecAlarmOnboardingPrompt = ({
  siteId,
  onDone,
  navigate,
  pollOptions,
  lookupTimeoutMs = 10_000,
}: {
  siteId: string | number;
  onDone: () => void;
  lookupTimeoutMs?: number;
  navigate?: NavigateFunction;
  pollOptions?: Omit<GenetecAlarmPollOptions, "signal">;
}) => {
  const [setup, setSetup] = React.useState<GenetecAlarmSetup | null>(null);
  const doneRef = React.useRef(false);
  const onDoneRef = React.useRef(onDone);
  onDoneRef.current = onDone;

  const finish = React.useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    setSetup(null);
    onDoneRef.current();
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    // Never hold up onboarding on a slow admin: give up and move on.
    const giveUp = setTimeout(() => {
      controller.abort();
      finish();
    }, lookupTimeoutMs);
    fetchGenetecAlarmSetup(siteId, controller.signal).then(
      (data) => {
        clearTimeout(giveUp);
        if (controller.signal.aborted) return;
        if (shouldPromptGenetecAlarmSetup(data)) {
          setSetup(data);
        } else {
          finish();
        }
      },
      () => {
        clearTimeout(giveUp);
        if (!controller.signal.aborted) finish();
      }
    );
    return () => {
      clearTimeout(giveUp);
      controller.abort();
    };
  }, [siteId, finish, lookupTimeoutMs]);

  if (!setup) return null;
  return (
    <GenetecAlarmSetupDialog
      open
      siteId={siteId}
      setup={setup}
      onClose={finish}
      navigate={navigate}
      pollOptions={pollOptions}
    />
  );
};
